import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'chakra-react-select';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Flex, Text } from '@chakra-ui/react';

import { IUser, useUsers } from '../../../hooks/useUsers';
import { IData } from '../../../types/shared';

// Autocomplete do "cliente" do token = um usuário REAL do sistema (tb_usuario), quem
// consome a API (auditoria/bloqueio). Molde do ClienteSelect do Estatuto (react-query +
// debounce + chakra-react-select, só SELEÇÃO). Sem filtro de grupo: lista qualquer
// usuário (a busca por nome/e-mail é server-side via ?search=).

export interface IUsuarioLite {
  id: number;
  nome: string;
  email?: string;
  imagem_url?: string;
}

interface Props {
  value: IUsuarioLite | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (usuario: IUsuarioLite | null) => void;
}

interface UsuarioOption {
  value: number;
  label: React.ReactNode;
  usuario: IUsuarioLite;
}

const toLite = (u: IUser): IUsuarioLite => ({
  id: u.id as number,
  nome: u.nome,
  email: u.email,
  imagem_url: u.imagem_url,
});

const usuarioLabel = (u: IUsuarioLite, compact = false): React.ReactNode => (
  <Flex align="center" gap={2} minW={0}>
    <Avatar size="xs" name={u.nome} src={u.imagem_url} />
    <Flex direction="column" minW={0} lineHeight="1.15">
      <Text fontSize="sm" fontWeight="600" noOfLines={1}>
        {u.nome}{' '}
        <Text as="span" fontSize="11px" color="gray.400" fontWeight="500">
          #{u.id}
        </Text>
      </Text>
      {!compact && u.email && (
        <Text fontSize="11px" color="gray.500" noOfLines={1}>
          {u.email}
        </Text>
      )}
    </Flex>
  </Flex>
);

export const ClienteUsuarioSelect: React.FC<Props> = ({ value, onChange }) => {
  const { getUsers } = useUsers();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const onInputChange = (val: string) => {
    setSearch(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(val), 400);
  };

  // Cancela o debounce pendente no unmount (evita setState em componente desmontado).
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const { data, isFetching } = useQuery<IData<IUser[]>>({
    queryKey: ['api-cliente-usuario-select', { search: debounced || undefined, page: 1, size: 20 }],
    queryFn: getUsers,
    enabled: menuOpen,
  });

  const options: UsuarioOption[] = useMemo(
    () =>
      (data?.data?.items ?? [])
        .filter(u => u.id != null)
        .map(u => {
          const lite = toLite(u);
          return { value: lite.id, label: usuarioLabel(lite), usuario: lite };
        }),
    [data],
  );

  return (
    <Select<UsuarioOption>
      placeholder="Buscar cliente por nome ou e-mail…"
      options={options}
      value={value ? { value: value.id, label: usuarioLabel(value, true), usuario: value } : null}
      inputValue={search}
      onInputChange={onInputChange}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => setMenuOpen(false)}
      onChange={opt => onChange(opt ? opt.usuario : null)}
      isLoading={isFetching}
      isClearable
      filterOption={null}
      noOptionsMessage={() => (debounced ? 'Nenhum usuário encontrado' : 'Digite para buscar')}
      loadingMessage={() => 'Buscando…'}
      size="sm"
    />
  );
};
